import { api } from './client.js';

const buildQuery = (params = {}) => {
  const clean = Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});
  const qs = new URLSearchParams(clean).toString();
  return qs ? `?${qs}` : '';
};

export const getCandidates = async (params = {}) => {
  return api.get(`/company/candidates${buildQuery(params)}`);
};

export const getCandidateById = async (id) => {
  if (!id) return Promise.reject(new Error('Candidate id is required'));
  return api.get(`/company/candidates/${id}`);
};

const buildCandidateFormData = (payload, resumeFile) => {
  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));
  if (resumeFile instanceof File) {
    formData.append('resume', resumeFile);
  }
  return formData;
};

export const createCandidate = async (payload, resumeFile = null) => {
  if (resumeFile instanceof File) {
    return api.post('/company/candidates', buildCandidateFormData(payload, resumeFile));
  }
  return api.post('/company/candidates', payload);
};

export const updateCandidate = async (id, payload, resumeFile = null) => {
  if (!id) return Promise.reject(new Error('Candidate id is required'));
  if (resumeFile instanceof File) {
    return api.put(`/company/candidates/${id}`, buildCandidateFormData(payload, resumeFile));
  }
  return api.put(`/company/candidates/${id}`, payload);
};

export const getCandidateResumeUrl = (id) => {
  const base = import.meta.env.VITE_API_BASE_URL || 'https://admin.jobscribe.in/api';
  return `${base.replace(/\/$/, '')}/company/candidates/${id}/resume`;
};

export const deleteCandidate = async (id) => {
  if (!id) return Promise.reject(new Error('Candidate id is required'));
  return api.delete(`/company/candidates/${id}`);
};

/** IDs for current list filters — used by select-all across pages. */
export const getCandidateIds = async (params = {}) => {
  return api.get(`/company/candidates/ids${buildQuery(params)}`);
};

export const bulkDeleteCandidates = async (ids) => {
  return api.post('/company/candidates/bulk/delete', { ids });
};

export const updateCandidateReviewStatus = async (id, reviewStatus) => {
  if (!id) return Promise.reject(new Error('Candidate id is required'));
  return api.patch(`/company/candidates/${id}/review-status`, { reviewStatus });
};

export const getCandidateRecommendations = async (id) => {
  if (!id) return Promise.reject(new Error('Candidate id is required'));
  return api.get(`/company/candidates/${id}/recommendations`);
};

export const getCandidateRecommendationsPaged = async (id, params = {}) => {
  if (!id) return Promise.reject(new Error('Candidate id is required'));
  const qs = buildQuery(params);
  return api.get(`/company/candidates/${id}/recommendations${qs}`);
};

export const getCandidateSavedJobs = async (id, params = {}) => {
  if (!id) return Promise.reject(new Error('Candidate id is required'));
  const qs = buildQuery(params);
  return api.get(`/company/candidates/${id}/saved-jobs${qs}`);
};

export const updateCandidateSavedJobPipeline = async (id, jobId, payload = {}, params = {}) => {
  if (!id || !jobId) return Promise.reject(new Error('Candidate id and job id are required'));
  const qs = buildQuery(params);
  return api.patch(`/company/candidates/${id}/saved-jobs/${jobId}/pipeline${qs}`, payload);
};

export const addCandidateSavedJob = async (id, jobId) => {
  if (!id || !jobId) return Promise.reject(new Error('Candidate id and job id are required'));
  return api.post(`/company/candidates/${id}/saved-jobs/${jobId}`, {});
};

export const removeCandidateSavedJob = async (id, jobId) => {
  if (!id || !jobId) return Promise.reject(new Error('Candidate id and job id are required'));
  return api.delete(`/company/candidates/${id}/saved-jobs/${jobId}`);
};
